/* Interactive Leaflet Map for Citizen Urban Vegetation Localization and Missions. */

import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

export interface LocalizedVegetation {
  id: string
  name: string
  species: string
  species_ar?: string
  lat: number
  lng: number
  district: string
  health: 'thriving' | 'needs-water' | 'monitored' | 'attention'
  health_score: number
  ndvi: number
  planted_date: string
  last_cared_at: string
  photo: string
  adopted_by_me?: boolean
  mission_title?: string
  care_history?: { date: string; action: string; points: number; notes?: string }[]
}

interface CitizenMapProps {
  plants: LocalizedVegetation[]
  selectedPlantId: string | null
  onSelectPlant: (plant: LocalizedVegetation) => void
  onQuickCare?: (plantId: string, action: 'water' | 'clean' | 'inspect') => void
  height?: number | string
  initialFilter?: 'all' | 'trees' | 'needs-care' | 'missions' | 'adopted'
}

export function CitizenMap({
  plants,
  selectedPlantId,
  onSelectPlant,
  height = 360,
  initialFilter = 'all',
}: CitizenMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<L.Map | null>(null)
  const markersLayerRef = useRef<L.LayerGroup | null>(null)
  const tileLayerRef = useRef<L.TileLayer | null>(null)
  const [basemap, setBasemap] = useState<'satellite' | 'street'>('satellite')
  const [filter, setFilter] = useState<'all' | 'trees' | 'needs-care' | 'missions' | 'adopted'>(initialFilter)

  // Filtered plant list
  const filteredPlants = plants.filter((p) => {
    if (filter === 'trees') return p.species.toLowerCase().includes('tree') || p.species.toLowerCase().includes('palm') || p.species.toLowerCase().includes('cedar')
    if (filter === 'needs-care') return p.health === 'needs-water' || p.health === 'attention' || p.health_score < 70
    if (filter === 'missions') return Boolean(p.mission_title)
    if (filter === 'adopted') return Boolean(p.adopted_by_me)
    return true
  })

  // 1. Initialize Leaflet Map
  useEffect(() => {
    if (!mapContainerRef.current) return
    if (mapInstanceRef.current) return

    // Center on urban Marrakech / Al Haouz area
    const map = L.map(mapContainerRef.current, {
      center: [31.633, -7.995],
      zoom: 14,
      zoomControl: true,
      attributionControl: false,
    })

    const tileUrl =
      basemap === 'satellite'
        ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
        : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'

    const tiles = L.tileLayer(tileUrl, { maxZoom: 19 }).addTo(map)
    tileLayerRef.current = tiles

    const markersGroup = L.layerGroup().addTo(map)
    markersLayerRef.current = markersGroup

    mapInstanceRef.current = map

    return () => {
      map.remove()
      mapInstanceRef.current = null
      markersLayerRef.current = null
    }
  }, [])

  // 2. Basemap toggler
  useEffect(() => {
    if (!mapInstanceRef.current || !tileLayerRef.current) return
    const url =
      basemap === 'satellite'
        ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
        : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    tileLayerRef.current.setUrl(url)
  }, [basemap])

  // 3. Render Custom Vegetation Markers
  useEffect(() => {
    const map = mapInstanceRef.current
    const group = markersLayerRef.current
    if (!map || !group) return

    group.clearLayers()

    filteredPlants.forEach((plant) => {
      const isSelected = plant.id === selectedPlantId
      const isStressed = plant.health === 'needs-water' || plant.health === 'attention'
      const hasMission = Boolean(plant.mission_title)

      let bg = '#15803d' // Healthy green
      let iconEmoji = '🌿'

      if (isStressed) {
        bg = '#ea580c' // Orange needs water
        iconEmoji = '💧'
      } else if (hasMission) {
        bg = '#2563eb' // Blue mission
        iconEmoji = '🎯'
      } else if (plant.species.toLowerCase().includes('palm')) {
        iconEmoji = '🌴'
      } else if (plant.species.toLowerCase().includes('tree')) {
        iconEmoji = '🌳'
      }

      const ringStyle = isSelected
        ? 'box-shadow: 0 0 0 4px #fbbf24, 0 4px 12px rgba(0,0,0,0.5); transform: scale(1.15); z-index: 1000;'
        : 'box-shadow: 0 3px 8px rgba(0,0,0,0.3);'

      const customIcon = L.divIcon({
        className: 'citizen-veg-pin',
        html: `
          <div style="
            background: ${bg};
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid #ffffff;
            font-size: 14px;
            transition: all 0.2s ease;
            cursor: pointer;
            ${ringStyle}
          ">
            <span>${iconEmoji}</span>
          </div>
        `,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      })

      const marker = L.marker([plant.lat, plant.lng], { icon: customIcon })

      marker.on('click', () => {
        onSelectPlant(plant)
      })

      // Popup
      const popupHtml = `
        <div style="font-family: inherit; font-size: 12px; color: #1e293b; min-width: 170px;">
          <b style="font-size: 13px; color: #14532d; display: block; margin-bottom: 2px;">${plant.name}</b>
          <div style="color: #64748b; margin-bottom: 4px;">${plant.species}</div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
            <span>Health: <b>${plant.health_score}%</b></span>
            <span>NDVI: <b>${plant.ndvi.toFixed(2)}</b></span>
          </div>
          <div style="font-size: 11px; color: #0f766e; background: #f0fdf4; padding: 3px 6px; border-radius: 6px; font-weight: 600;">
            📍 ${plant.district}
          </div>
        </div>
      `
      marker.bindPopup(popupHtml, { offset: [0, -10] })

      group.addLayer(marker)
    })
  }, [filteredPlants, selectedPlantId, onSelectPlant])

  // Center on selected plant
  useEffect(() => {
    const map = mapInstanceRef.current
    if (!map || !selectedPlantId) return
    const plant = plants.find((p) => p.id === selectedPlantId)
    if (plant) {
      map.panTo([plant.lat, plant.lng], { animate: true, duration: 0.6 })
    }
  }, [selectedPlantId, plants])

  const handleLocateMe = () => {
    if (!mapInstanceRef.current) return
    // Default to center of urban activity with animation
    mapInstanceRef.current.setView([31.633, -7.995], 15, { animate: true })
  }

  return (
    <div style={{ position: 'relative', width: '100%', height, borderRadius: '20px', overflow: 'hidden', border: '1px solid var(--sage-100, #C2D8C6)' }}>
      {/* Top Map Controls */}
      <div
        style={{
          position: 'absolute',
          top: 10,
          left: 10,
          right: 10,
          zIndex: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 6,
          pointerEvents: 'none',
        }}
      >
        {/* Layer Filter Chips */}
        <div style={{ display: 'flex', gap: 5, overflowX: 'auto', pointerEvents: 'auto', padding: '2px 0' }}>
          {[
            ['all', `All (${plants.length})`],
            ['trees', '🌲 Trees'],
            ['needs-care', '💧 Needs Care'],
            ['missions', '🎯 Missions'],
            ['adopted', '⭐ My Plants'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key as any)}
              style={{
                background: filter === key ? 'var(--forest-800, #1b382b)' : 'rgba(255,255,255,0.92)',
                color: filter === key ? '#ffffff' : 'var(--forest-900, #14281d)',
                border: '1px solid rgba(0,0,0,0.1)',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                whiteSpace: 'nowrap',
                backdropFilter: 'blur(4px)',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Basemap toggle & Locate Button */}
        <div style={{ display: 'flex', gap: 5, pointerEvents: 'auto' }}>
          <button
            type="button"
            onClick={() => setBasemap(basemap === 'satellite' ? 'street' : 'satellite')}
            style={{
              background: 'rgba(255,255,255,0.95)',
              border: '1px solid rgba(0,0,0,0.15)',
              borderRadius: '12px',
              padding: '4px 8px',
              fontSize: '11px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            }}
          >
            {basemap === 'satellite' ? '🛰️ Sat' : '🗺️ Map'}
          </button>
          <button
            type="button"
            title="Center on my location"
            onClick={handleLocateMe}
            style={{
              background: 'rgba(255,255,255,0.95)',
              border: '1px solid rgba(0,0,0,0.15)',
              borderRadius: '12px',
              padding: '4px 8px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            }}
          >
            📍
          </button>
        </div>
      </div>

      {/* Leaflet DOM container */}
      <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

      {/* Map Footer Banner */}
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: 10,
          right: 10,
          zIndex: 500,
          background: 'rgba(255,255,255,0.92)',
          borderRadius: '10px',
          padding: '4px 10px',
          fontSize: '11px',
          fontWeight: 600,
          color: 'var(--forest-800, #14532d)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backdropFilter: 'blur(4px)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        }}
      >
        <span>📍 Tap any plant on the map to inspect vegetation NDVI & care</span>
        <span style={{ fontSize: '10px', color: '#64748b' }}>GPS Localization Active</span>
      </div>
    </div>
  )
}
